import { useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useReduxDispatch } from '../../app/store';
import { getSites } from '../../features/sites/sitesSlice';
import { checkUserLoggedIn, fetchUserDetails } from '../../features/user/userSlice';
import { APP_VERSION, IS_NOT_DEVELOPMENT, IS_PRODUCTION, IS_RUNNING_NATIVE_TESTS } from '../constants';

type CognitoUserWithSession = {
  signInUserSession: {
    idToken: {
      payload: {
        [key: string]: unknown;
      };
    };
  };
};

export function useAppReady() {
  const [isLoaded, setLoaded] = useState(false);
  const dispatch = useReduxDispatch();
  const isFirstRender = useRef(true);
  const toast = useToast({ isClosable: true });
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkUserStatus = async () => {
      const { isLoggedIn, cognitoUser } = unwrapResult(await dispatch(checkUserLoggedIn()));

      // User is logged into the cIAM session not the newer EIM (EcoStruxure Platform Identity Module) session
      // No choice but to log them out as without a refresh token, we can't get a new access token
      if (isLoggedIn && cognitoUser != null) {
        // NOTE: have to use any type as `CognitoUser` type from amazon-cognito-identity-js does not have correct properties
        const payload = (cognitoUser as unknown as CognitoUserWithSession).signInUserSession.idToken.payload;
        const externalIdpRefreshToken = payload['custom:refresh_token'];
        const externalIdpIdToken = payload['custom:id_token'];
        const hasExternalIdpTokens = !!externalIdpRefreshToken && !!externalIdpIdToken;
        if (!IS_RUNNING_NATIVE_TESTS && !hasExternalIdpTokens) {
          navigate('/logout');
          return;
        }
      }
      await dispatch(getSites({ pageSize: 2, offset: 0 }));

      if (isLoggedIn && isFirstRender.current) {
        isFirstRender.current = false;
        const userDetailsResponse = await dispatch(
          fetchUserDetails({
            isProduction: IS_PRODUCTION,
            isNotDevelopment: IS_NOT_DEVELOPMENT,
            version: APP_VERSION,
          })
        );
        if (userDetailsResponse.meta.requestStatus === 'rejected') {
          toast({
            title: t('Common.unable to fetch user details'),
            description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
            status: 'error',
          });
        }
      }
      setLoaded(true);
    };

    checkUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isLoaded;
}
